import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { RunDetail as RunDetailType, Span } from "@agentdash/shared";
import { useRegenerateSummary, useRunDetail } from "../../store/hooks.js";
import { StatusDot } from "../../components/StatusDot.js";
import { Elapsed } from "../../components/Elapsed.js";
import { Timestamp } from "../../components/Timestamp.js";
import { formatAbsolute, formatClockMs, formatDurationMs, formatRelative } from "../../lib/time.js";
import { buildIndentMap } from "./indent-map.js";

function TraceRow({ traceId }: { traceId: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="kv" style={{ display: "flex", alignItems: "center", gap: 8 }}>
      trace{" "}
      <b style={{ fontFamily: "var(--mono, monospace)", wordBreak: "break-all" }}>
        {traceId}
      </b>
      <button
        type="button"
        title="Copy trace id"
        onClick={() => {
          navigator.clipboard?.writeText(traceId).then(
            () => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1200);
            },
            () => {},
          );
        }}
        style={{
          background: "transparent",
          color: "var(--muted)",
          border: "1px solid var(--border)",
          borderRadius: 4,
          padding: "0 8px",
          fontSize: 11,
          cursor: "pointer",
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function Header({ run }: { run: RunDetailType }) {
  return (
    <div className="panel">
      <div className="panel-header">
        <StatusDot status={run.status} /> {run.name}
      </div>
      <div style={{ padding: 16, display: "grid", gap: 4 }}>
        <div className="kv">
          status <b>{run.status}</b>
        </div>
        <TraceRow traceId={run.trace_id} />
        <div className="kv">
          source <b>{run.source}</b>
        </div>
        <div className="kv">
          service <b>{run.service_name}</b>
        </div>
        <div className="kv">
          host <b>{run.host ?? "—"}</b>
        </div>
        <div className="kv">
          repo <b>{run.repo ?? "—"}</b>
        </div>
        <div className="kv">
          started <b><Timestamp iso={run.started_at} /></b>
        </div>
        <div className="kv">
          {run.status === "running" ? (
            <>
              elapsed <b><Elapsed startedAt={run.started_at} /></b>
            </>
          ) : (
            <>
              duration <b>{formatDurationMs(run.duration_ms)}</b>
            </>
          )}
        </div>
        <div className="kv">
          exit code <b>{run.exit_code ?? "—"}</b>
        </div>
      </div>
    </div>
  );
}

function Summary({ run }: { run: RunDetailType }) {
  const regen = useRegenerateSummary(run.trace_id);
  const status = run.summary_status;
  const isTerminal = run.status === "ok" || run.status === "error";
  // Allow regenerating even while pending: a summary stuck in pending (its
  // worker died mid-call) is otherwise unrecoverable from the UI. Against a
  // genuinely in-flight summary the server answers 409, which the alert below
  // renders, so the click is never silently dropped.
  const canRegenerate = isTerminal;

  return (
    <section className="panel">
      <div
        className="panel-header"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <span>Summary</span>
        {canRegenerate && (
          <button
            type="button"
            onClick={() => regen.mutate()}
            disabled={regen.isPending}
            style={{
              background: "transparent",
              color: "var(--muted)",
              border: "1px solid var(--border)",
              borderRadius: 4,
              padding: "2px 10px",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            {regen.isPending
              ? "Queuing…"
              : status === "ok" || status === "pending"
                ? "Regenerate"
                : "Generate"}
          </button>
        )}
      </div>
      <div style={{ padding: 16, display: "grid", gap: 8 }}>
        {regen.error && (
          // The summarize POST is fire-and-forget: a 503 (service not
          // initialised) or network fault otherwise leaves the button
          // re-enabled with no feedback, so the operator assumes it queued.
          <div role="alert" style={{ color: "var(--err)", fontSize: 12 }}>
            Regenerate failed:{" "}
            {String((regen.error as Error).message ?? regen.error)}
          </div>
        )}
        {!isTerminal ? (
          <div style={{ color: "var(--muted)" }}>
            Summary will be generated once the run completes.
          </div>
        ) : status === "pending" ? (
          <div style={{ color: "var(--muted)" }}>Summarizing…</div>
        ) : status === "error" ? (
          <div style={{ color: "var(--err)", whiteSpace: "pre-wrap" }}>
            Summarization failed: {run.summary_error ?? "unknown error"}
          </div>
        ) : run.summary ? (
          <>
            <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{run.summary}</div>
            <div style={{ color: "var(--muted)", fontSize: 12 }}>
              {run.summary_model ?? "—"}
              {run.summary_generated_at &&
                ` · generated ${formatRelative(run.summary_generated_at)}`}
            </div>
          </>
        ) : (
          <div style={{ color: "var(--muted)" }}>No summary yet.</div>
        )}
      </div>
    </section>
  );
}

function Timeline({ spans, truncated }: { spans: Span[]; truncated: boolean }) {
  if (spans.length === 0) {
    return <div style={{ padding: 16, color: "var(--muted)" }}>No spans.</div>;
  }
  const indents = buildIndentMap(spans);
  return (
    <>
      {truncated && (
        <div
          style={{
            padding: "8px 16px",
            color: "var(--muted)",
            fontSize: 12,
            borderBottom: "1px solid var(--border)",
          }}
        >
          Showing the oldest {spans.length.toLocaleString()} spans — later spans were
          capped server-side.
        </div>
      )}
      <div className="timeline" style={{ padding: 16 }}>
      {spans.map((s) => (
        <div
          key={s.span_id}
          className="timeline-row"
          style={{ paddingLeft: (indents.get(s.span_id) ?? 0) * 16 }}
        >
          <StatusDot status={s.status === "ok" ? "ok" : s.status === "error" ? "error" : "unknown"} />
          {s.name}{" "}
          <span style={{ color: "var(--muted)" }}>
            ({formatDurationMs(s.duration_ms)})
          </span>
        </div>
      ))}
      </div>
    </>
  );
}

const ERROR_SEVERITIES = new Set(["warn", "error", "fatal"]);

function Events({
  events,
  follow,
  truncated,
}: {
  events: RunDetailType["events"];
  follow: boolean;
  truncated: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const visible = useMemo(
    () =>
      errorsOnly
        ? events.filter((e) => e.severity && ERROR_SEVERITIES.has(e.severity))
        : events,
    [events, errorsOnly],
  );
  useEffect(() => {
    if (follow && ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  }, [visible, follow]);

  return (
    <section className="panel">
      <div
        className="panel-header"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <span>
          Events
          {errorsOnly && (
            <span style={{ color: "var(--muted)", fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
              showing {visible.length} of {events.length}
            </span>
          )}
        </span>
        {events.length > 0 && (
          <button
            type="button"
            onClick={() => setErrorsOnly((v) => !v)}
            title={
              errorsOnly
                ? "Show all events"
                : "Show only warn/error/fatal events — hides info/debug/trace and span events that have no severity"
            }
            style={{
              background: errorsOnly ? "var(--err)" : "transparent",
              color: errorsOnly ? "#fff" : "var(--muted)",
              border: "1px solid var(--border)",
              borderRadius: 4,
              padding: "2px 10px",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            {errorsOnly ? "Showing errors" : "Errors only"}
          </button>
        )}
      </div>
      {truncated && (
        <div
          style={{
            padding: "8px 16px",
            color: "var(--muted)",
            fontSize: 12,
            borderBottom: "1px solid var(--border)",
          }}
        >
          Showing the newest {events.length.toLocaleString()} events — older events were
          capped server-side.
        </div>
      )}
      {events.length === 0 ? (
        <div style={{ padding: 16, color: "var(--muted)" }}>No events yet.</div>
      ) : visible.length === 0 ? (
        <div style={{ padding: 16, color: "var(--muted)" }}>
          No warn/error/fatal events in this run.
        </div>
      ) : (
        <div
          ref={ref}
          className="events"
          style={{ padding: 16, maxHeight: 480, overflowY: "auto" }}
        >
          {visible.map((e) => {
            const sevClass = e.severity ? `sev-${e.severity}` : "";
            return (
              <div key={e.id}>
                <span className="ts" title={formatAbsolute(e.timestamp)}>
                  {formatClockMs(e.timestamp)}
                </span>
                {e.severity && <span className={sevClass}>[{e.severity}] </span>}
                <span>{e.body}</span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function RunDetail() {
  const { traceId } = useParams<{ traceId: string }>();
  const q = useRunDetail(traceId ?? "");

  if (!traceId) return <div style={{ padding: 16 }}>Missing trace id.</div>;
  if (q.isLoading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (q.isError)
    return (
      <div style={{ padding: 16, color: "var(--err)" }}>
        Failed to load: {String((q.error as Error).message ?? q.error)}
      </div>
    );
  if (!q.data) return <div style={{ padding: 16 }}>Not found.</div>;

  const isRunning = q.data.status === "running";

  return (
    <>
      <Header run={q.data} />
      <Summary run={q.data} />
      <section className="panel">
        <div className="panel-header">Timeline</div>
        <Timeline spans={q.data.spans} truncated={q.data.spans_truncated} />
      </section>
      <Events events={q.data.events} follow={isRunning} truncated={q.data.events_truncated} />
    </>
  );
}
